import axios from 'axios';
import config from '../config';
import httpStatusCodes from '../constants/http-status-codes';
import { parseHttpStatusText } from '../tools';
import unauthorizedSubcodes from '../constants/unauthorized-subcodes';
import { signOut, setUserFromAuthData } from '../actions/auth-user';
import handleError from './error-helper';

let apiFetcherStore = null;

export function setApiFetcherStore(store) {
  apiFetcherStore = store;
}

class ApiFetcher {
  constructor() {
    this.options = {
      baseURL:
        (config.webApi.secure ? 'https://' : 'http://') +
        config.webApi.host +
        config.webApi.path,
      headers: {
        'Content-Type': 'application/json'
      }
    };

    let instance = axios.create(this.options);
    instance.auth = this.auth.bind(this);
    instance.refreshAuthTokens = this.refreshAuthTokens.bind(this);
    instance.getFullApiUrl = this.getFullApiUrl.bind(this);
    instance.getSchoolLogoUrl = this.getSchoolLogoUrl.bind(this);
    instance.getSchoolCoverPhotoUrl = this.getSchoolCoverPhotoUrl.bind(this);
    instance.getCandidateProfilePhotoUrl = this.getCandidateProfilePhotoUrl.bind(
      this
    );
    instance.getPublicVacancyDocumentUrl = this.getPublicVacancyDocumentUrl.bind(
      this
    );
    instance.getVacancyDocumentUrl = this.getVacancyDocumentUrl.bind(this);
    instance.getSchoolCandidateProfileDocumentUrl = this.getSchoolCandidateProfileDocumentUrl.bind(
      this
    );
    instance.getCurrentCandidateProfileDocumentUrl = this.getCurrentCandidateProfileDocumentUrl.bind(
      this
    );

    return instance;
  }

  getFullApiUrl(path = '') {
    const { baseURL } = this.options;

    return `${baseURL}${path}`;
  }

  getSchoolLogoUrl(schoolId) {
    return this.getFullApiUrl(`public/schools/${schoolId}/logo`);
  }

  getSchoolCoverPhotoUrl(schoolId) {
    return this.getFullApiUrl(`public/schools/${schoolId}/cover-photo`);
  }

  getCandidateProfilePhotoUrl(candidateId) {
    return this.getFullApiUrl(
      `public/candidates/${candidateId}/profile/photo?t=${Date.now()}`
    );
  }

  getPublicVacancyDocumentUrl(schoolId, vacancyId, documentId) {
    return this.getFullApiUrl(
      `public/schools/${schoolId}/vacancies/${vacancyId}/documents/${documentId}`
    );
  }

  getVacancyDocumentUrl(schoolId, vacancyId, documentId) {
    return this.getFullApiUrl(
      `schools/${schoolId}/vacancies/${vacancyId}/documents/${documentId}`
    );
  }

  getSchoolCandidateProfileDocumentUrl(schoolId, candidateId, documentId) {
    return this.getFullApiUrl(
      `schools/${schoolId}/candidates/${candidateId}/profile/documents/${documentId}`
    );
  }

  getCurrentCandidateProfileDocumentUrl(documentId) {
    return this.getFullApiUrl(
      `candidates/current/profile/documents/${documentId}`
    );
  }

  auth(nativeAuthToken) {
    const authToken =
      nativeAuthToken ||
      apiFetcherStore.getState().authUser.get('nativeAuthToken');

    let options = this.options;

    if (authToken) {
      options.headers = {
        ...this.options.headers,
        Authorization: this.createAuthorizationHeader(authToken)
      };
    } else {
      console.warn('Token is not set yet.');
    }

    let instance = axios.create(this.options);

    instance.interceptors.response.use(undefined, error => {
      try {
        const { status, statusText } = error.response;

        if (status === httpStatusCodes.Unauthorized) {
          const originalRequest = error.config;
          const { subcode } = parseHttpStatusText(statusText);

          if (subcode === unauthorizedSubcodes.RefreshToken) {
            return this.auth(authToken)
              .get('auth-tokens/next')
              .then(({ data }) => {
                apiFetcherStore.dispatch(setUserFromAuthData(data));
                originalRequest.headers[
                  'Authorization'
                ] = this.createAuthorizationHeader(data.nativeAuthToken);
                return axios(originalRequest);
              });
          } else {
            apiFetcherStore.dispatch(signOut());
          }
        }

        return Promise.reject(error);
      } catch (exception) {
        handleError(exception);
        return Promise.reject(exception);
      }
    });

    return instance;
  }

  refreshAuthTokens() {
    this.auth().get('auth-tokens/next')
      .then(({ data }) => {
        apiFetcherStore.dispatch(setUserFromAuthData(data));
      });
  }

  createAuthorizationHeader(token) {
    return `Bearer ${token}`;
  }
}

export default new ApiFetcher();



// WEBPACK FOOTER //
// ./src/tools/api-fetcher.js